/**
 * OperatorPerformancePanel.tsx — MER Algo Footprints v7.1B — Phase 7.1B
 *
 * features/71b/OperatorPerformancePanel.tsx — Phase 7.1B
 *
 * Derived metrics for operator behavior.
 * Purely descriptive — all aggregations are read-only over journal71aEntries.
 * No engine feedback. No ranking modification.
 *
 * Views:
 *   Overview   — selection rate, review rate, note rate, time-of-day heat
 *   By Symbol  — per-symbol behavior breakdown
 *   By Setup   — which setups are most frequently taken vs skipped
 *   By Regime  — action distribution per regime
 *   Insights   — frequently skipped high-score / taken low-score patterns
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import { Journal71AEntry } from '../../types';

// ─── Shared helpers ───────────────────────────────────────────────────────────

function pct(n: number, d: number): string {
  return d === 0 ? '—' : `${Math.round(n / d * 100)}%`;
}

function scoreColor(v: number): string {
  return v >= 70 ? 'var(--green)' : v >= 45 ? 'var(--amber)' : 'var(--red)';
}

const TH: React.CSSProperties = {
  padding: '4px 8px', fontSize: 9, color: 'var(--t3)',
  textTransform: 'uppercase', letterSpacing: '.7px',
  borderBottom: '1px solid var(--border)', textAlign: 'left',
};
const TD: React.CSSProperties = {
  padding: '4px 8px', fontSize: 10, color: 'var(--text)',
  borderBottom: '1px solid var(--surface)', fontFamily: 'var(--mono)',
};
const TDR: React.CSSProperties = { ...TD, textAlign: 'right' };

// ─── KPI card ─────────────────────────────────────────────────────────────────

function KPI({ label, value, sub, color = 'var(--text)' }: {
  label: string; value: string | number; sub?: string; color?: string;
}) {
  return (
    <div style={{
      background: 'var(--surface2)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '8px 10px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '.8px', marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color, fontFamily: 'var(--mono)' }}>{value}</div>
      {sub && <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Behavior breakdown row ───────────────────────────────────────────────────

interface BehaviorRow {
  key:          string;
  total:        number;
  taken:        number;
  skipped:      number;
  selectionRate: string;   // taken / (taken + skipped)
  reviewRate:   string;    // outcomes / total
  noteRate:     string;    // notes / total
  outcomes:     number;
  notes:        number;
  avgScore:     number | null;
}

function buildBehaviorRows(
  entries: Journal71AEntry[],
  keyFn: (e: Journal71AEntry) => string | null,
): BehaviorRow[] {
  const map = new Map<string, {
    total: number; taken: number; skipped: number;
    outcomes: number; notes: number; scores: number[];
  }>();

  for (const e of entries) {
    const k = keyFn(e) ?? '—';
    if (!map.has(k)) map.set(k, { total: 0, taken: 0, skipped: 0, outcomes: 0, notes: 0, scores: [] });
    const r = map.get(k)!;
    r.total++;
    if (e.actionType === 'trade_taken')      r.taken++;
    if (e.actionType === 'trade_skipped')    r.skipped++;
    if (e.actionType === 'outcome_reviewed') r.outcomes++;
    if (e.actionType === 'note_added')       r.notes++;
    if (e.score !== null)                    r.scores.push(e.score);
  }

  return [...map.entries()].map(([key, d]) => {
    const decided = d.taken + d.skipped;
    const avgScore = d.scores.length
      ? Math.round(d.scores.reduce((a, b) => a + b, 0) / d.scores.length)
      : null;
    return {
      key,
      total:         d.total,
      taken:         d.taken,
      skipped:       d.skipped,
      selectionRate: pct(d.taken, decided),
      reviewRate:    pct(d.outcomes, d.total),
      noteRate:      pct(d.notes, d.total),
      outcomes:      d.outcomes,
      notes:         d.notes,
      avgScore,
    };
  }).sort((a, b) => b.total - a.total);
}

function BehaviorTable({ rows, keyLabel }: { rows: BehaviorRow[]; keyLabel: string }) {
  if (!rows.length) return (
    <div style={{ fontSize: 10, color: 'var(--t3)', padding: '12px 0', fontStyle: 'italic' }}>
      No data yet — log TAKEN or SKIPPED actions to populate this view.
    </div>
  );
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={TH}>{keyLabel}</th>
            <th style={{ ...TH, textAlign: 'right' }}>Total</th>
            <th style={{ ...TH, textAlign: 'right' }}>Taken</th>
            <th style={{ ...TH, textAlign: 'right' }}>Skipped</th>
            <th style={{ ...TH, textAlign: 'right' }}>Sel%</th>
            <th style={{ ...TH, textAlign: 'right' }}>Review%</th>
            <th style={{ ...TH, textAlign: 'right' }}>Note%</th>
            <th style={{ ...TH, textAlign: 'right' }}>Avg Score</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.key}>
              <td style={TD}>{r.key}</td>
              <td style={TDR}>{r.total}</td>
              <td style={{ ...TDR, color: r.taken > 0 ? 'var(--green)' : 'var(--t3)' }}>{r.taken}</td>
              <td style={{ ...TDR, color: r.skipped > 0 ? 'var(--amber)' : 'var(--t3)' }}>{r.skipped}</td>
              <td style={TDR}>{r.selectionRate}</td>
              <td style={{ ...TDR, color: r.reviewRate !== '—' ? 'var(--purple)' : 'var(--t3)' }}>{r.reviewRate}</td>
              <td style={{ ...TDR, color: r.noteRate !== '—' ? 'var(--cyan)' : 'var(--t3)' }}>{r.noteRate}</td>
              <td style={{ ...TDR, color: r.avgScore !== null ? scoreColor(r.avgScore) : 'var(--t3)' }}>
                {r.avgScore ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Insights view ────────────────────────────────────────────────────────────

function InsightsView({ entries }: { entries: Journal71AEntry[] }) {
  // Frequently skipped high-score setups (score >= 65, skipped)
  const highScoreSkipped = useMemo(() => {
    const skipped = entries.filter(e => e.actionType === 'trade_skipped' && (e.score ?? 0) >= 65);
    const map = new Map<string, { count: number; scores: number[] }>();
    for (const e of skipped) {
      const k = `${e.symbol} — ${e.setupType?.replace(/_/g,' ') ?? '?'}`;
      if (!map.has(k)) map.set(k, { count: 0, scores: [] });
      const r = map.get(k)!;
      r.count++;
      if (e.score !== null) r.scores.push(e.score);
    }
    return [...map.entries()]
      .map(([k, d]) => ({
        key: k, count: d.count,
        avgScore: d.scores.length ? Math.round(d.scores.reduce((a,b)=>a+b,0)/d.scores.length) : null,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [entries]);

  // Frequently taken low-score setups (score < 45, taken)
  const lowScoreTaken = useMemo(() => {
    const taken = entries.filter(e => e.actionType === 'trade_taken' && e.score !== null && e.score < 45);
    const map = new Map<string, { count: number; scores: number[] }>();
    for (const e of taken) {
      const k = `${e.symbol} — ${e.setupType?.replace(/_/g,' ') ?? '?'}`;
      if (!map.has(k)) map.set(k, { count: 0, scores: [] });
      const r = map.get(k)!;
      r.count++;
      if (e.score !== null) r.scores.push(e.score);
    }
    return [...map.entries()]
      .map(([k, d]) => ({
        key: k, count: d.count,
        avgScore: d.scores.length ? Math.round(d.scores.reduce((a,b)=>a+b,0)/d.scores.length) : null,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [entries]);

  // Time-of-day activity heatmap (hour buckets)
  const hourMap = useMemo(() => {
    const m = new Array(24).fill(0);
    for (const e of entries) {
      const h = new Date(e.ts).getHours();
      m[h]++;
    }
    return m;
  }, [entries]);

  const maxHour = Math.max(...hourMap, 1);

  return (
    <div>
      {/* Time of day */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 6 }}>
          Activity by Hour
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 40 }}>
          {hourMap.map((count, h) => (
            <div key={h} title={`${h}:00 — ${count} entries`} style={{
              flex: 1, background: count > 0 ? 'var(--gold)' : 'var(--surface)',
              opacity: count > 0 ? 0.3 + 0.7 * (count / maxHour) : 1,
              borderRadius: '2px 2px 0 0',
              height: count > 0 ? `${Math.max(4, Math.round(count / maxHour * 36))}px` : '4px',
              border: '1px solid var(--border)',
              cursor: 'default',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 7,
          color: 'var(--t3)', fontFamily: 'var(--mono)', marginTop: 2 }}>
          {['0', '6', '12', '18', '23'].map(h => <span key={h}>{h}h</span>)}
        </div>
      </div>

      {/* High-score skipped */}
      {highScoreSkipped.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, color: 'var(--amber)', fontFamily: 'var(--mono)',
            letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 5 }}>
            ⚠ Frequently Skipped High-Score (≥65)
          </div>
          {highScoreSkipped.map(r => (
            <div key={r.key} style={{ display: 'flex', gap: 8, padding: '3px 0',
              borderBottom: '1px solid var(--surface)', fontSize: 10 }}>
              <span style={{ flex: 1, color: 'var(--text)' }}>{r.key}</span>
              <span style={{ color: 'var(--amber)', fontFamily: 'var(--mono)', fontWeight: 700 }}>
                {r.count}×
              </span>
              <span style={{ color: r.avgScore !== null ? scoreColor(r.avgScore) : 'var(--t3)',
                fontFamily: 'var(--mono)', minWidth: 28, textAlign: 'right' }}>
                {r.avgScore ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Low-score taken */}
      {lowScoreTaken.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, color: 'var(--red)', fontFamily: 'var(--mono)',
            letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 5 }}>
            ⚠ Frequently Taken Low-Score (&lt;45)
          </div>
          {lowScoreTaken.map(r => (
            <div key={r.key} style={{ display: 'flex', gap: 8, padding: '3px 0',
              borderBottom: '1px solid var(--surface)', fontSize: 10 }}>
              <span style={{ flex: 1, color: 'var(--text)' }}>{r.key}</span>
              <span style={{ color: 'var(--red)', fontFamily: 'var(--mono)', fontWeight: 700 }}>
                {r.count}×
              </span>
              <span style={{ color: r.avgScore !== null ? scoreColor(r.avgScore) : 'var(--t3)',
                fontFamily: 'var(--mono)', minWidth: 28, textAlign: 'right' }}>
                {r.avgScore ?? '—'}
              </span>
            </div>
          ))}
        </div>
      )}

      {highScoreSkipped.length === 0 && lowScoreTaken.length === 0 && (
        <div style={{ fontSize: 10, color: 'var(--t3)', padding: '8px 0' }}>
          No notable patterns yet — more data needed.
        </div>
      )}
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

type PerfView = 'overview' | 'symbol' | 'setup' | 'regime' | 'insights';

export function OperatorPerformancePanel() {
  const entries = useStore(s => s.journal71aEntries);
  const [view, setView] = useState<PerfView>('overview');

  const total    = entries.length;
  const taken    = useMemo(() => entries.filter(e => e.actionType === 'trade_taken').length,      [entries]);
  const skipped  = useMemo(() => entries.filter(e => e.actionType === 'trade_skipped').length,    [entries]);
  const outcomes = useMemo(() => entries.filter(e => e.actionType === 'outcome_reviewed').length, [entries]);
  const notes    = useMemo(() => entries.filter(e => e.actionType === 'note_added').length,       [entries]);
  const decided  = taken + skipped;

  const bySymbol = useMemo(() => buildBehaviorRows(entries, e => e.symbol),    [entries]);
  const bySetup  = useMemo(() => buildBehaviorRows(entries, e => e.setupType), [entries]);
  const byRegime = useMemo(() => buildBehaviorRows(entries, e => e.regime),    [entries]);

  const btnStyle = (active: boolean, color = 'var(--gold)'): React.CSSProperties => ({
    fontSize: 9, fontFamily: 'var(--mono)', fontWeight: active ? 700 : 400,
    color: active ? color : 'var(--t3)',
    background: active ? `${color}12` : 'none',
    border: `1px solid ${active ? `${color}55` : 'var(--border)'}`,
    padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
  });

  if (!total) {
    return (
      <div style={{ padding: '28px 0', color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 8 }}>
          📈 Operator Performance
        </div>
        <div style={{ fontSize: 11, lineHeight: 1.7 }}>
          No activity logged yet.<br />
          <span style={{ color: 'var(--blue-dim)' }}>
            Log TAKEN or SKIPPED actions via ▼ ATTRIBUTION<br />
            to begin tracking selection rate, review rate, and behavior by symbol.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Overview KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 5, marginBottom: 12 }}>
        <KPI label="Total"      value={total}                   color="var(--blue-muted)" />
        <KPI label="Sel. Rate"  value={pct(taken, decided)}     color="var(--green)"
          sub={`${taken}T / ${skipped}S`} />
        <KPI label="Review Rate" value={pct(outcomes, total)}   color="var(--purple)"
          sub={`${outcomes} outcomes`} />
        <KPI label="Note Rate"  value={pct(notes, total)}       color="var(--cyan)"
          sub={`${notes} notes`} />
        <KPI label="Decided"    value={decided}                 color="var(--t2)"
          sub={`${pct(decided, total)} of total`} />
      </div>

      {/* Sub-view tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 10, flexWrap: 'wrap' }}>
        {(['overview','symbol','setup','regime','insights'] as PerfView[]).map(v => (
          <button key={v} style={btnStyle(view === v)} onClick={() => setView(v)}>
            {v === 'overview' ? 'Overview' : v === 'symbol' ? 'Symbol'
              : v === 'setup' ? 'Setup' : v === 'regime' ? 'Regime' : '⚡ Insights'}
          </button>
        ))}
      </div>

      {view === 'overview' && (
        <>
          <div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 8 }}>
            Top symbols by activity:
          </div>
          <BehaviorTable rows={bySymbol.slice(0, 6)} keyLabel="Symbol" />
        </>
      )}
      {view === 'symbol'   && <BehaviorTable rows={bySymbol} keyLabel="Symbol"     />}
      {view === 'setup'    && <BehaviorTable rows={bySetup}  keyLabel="Setup Type" />}
      {view === 'regime'   && <BehaviorTable rows={byRegime} keyLabel="Regime"     />}
      {view === 'insights' && <InsightsView entries={entries} />}
    </div>
  );
}
